import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Warehouse, Lock, ArrowLeft, Plus, Trash2, RefreshCw, AlertTriangle } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { cn } from '../../utils/cn';
import type { DespatchArea, NurseryLocation, NurseryStockItem, RefillRule } from '../../types/nursery';

/**
 * /nursery/refills — despatch areas and the rules that keep them stocked.
 *
 * A despatch area is a location pickers draw from. For each tree kept there
 * the nursery sets the level the area should hold and how many to bring at a
 * time; once the area is a whole refill below that level, a refill transfer
 * raises itself from the growing farms. Checked after every despatch and
 * every count correction at the area, and whenever someone presses Check now.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

interface RuleDraft { stock_item_id: number | ''; target_quantity: number | ''; refill_quantity: number | ''; source_farm_id: number | '' }
const EMPTY: RuleDraft = { stock_item_id: '', target_quantity: '', refill_quantity: '', source_farm_id: '' };

function DespatchAreas() {
  const navigate = useNavigate();
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);
  const [areas, setAreas] = useState<DespatchArea[] | null>(null);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [stockItems, setStockItems] = useState<NurseryStockItem[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [newArea, setNewArea] = useState<number | ''>('');
  const [drafts, setDrafts] = useState<Record<number, RuleDraft>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r, locs, items, pick] = await Promise.all([
        nurseryService.getRefills(),
        nurseryService.getLocations({ active: true }),
        nurseryService.getStockItems({ active: true }),
        nurseryService.getPickers(),
      ]);
      if (r.success) setAreas(r.data);
      else setError(errorMessage(r, 'Could not load despatch areas'));
      if (locs.success) setLocations(locs.data || []);
      if (items.success) setStockItems(items.data || []);
      if (pick.success) setFarms(pick.data.farms);
    } catch (e) {
      setError(errorMessage(e, 'Could not load despatch areas'));
    }
  }, []);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addRule = async (area: DespatchArea) => {
    const d = drafts[area.id] ?? EMPTY;
    if (!d.stock_item_id || !d.target_quantity || !d.refill_quantity) {
      setError('Choose the tree, the level the area holds and how many to bring at a time.');
      return;
    }
    const ok = await run(async () => {
      const res = await nurseryService.saveRefillRule({
        location_id: area.id, stock_item_id: d.stock_item_id as number,
        target_quantity: Number(d.target_quantity), refill_quantity: Number(d.refill_quantity),
        source_farm_id: d.source_farm_id || null,
      });
      if (!res.success) throw new Error(errorMessage(res, 'Could not save the rule'));
    }, 'Could not save the rule');
    if (ok) setDrafts((x) => ({ ...x, [area.id]: EMPTY }));
  };

  if (licenseLoading) return <div className="max-w-3xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery Manager license required</h2>
          <p className="text-sm text-neutral-500 mb-4">Setting up despatch areas needs a Nursery Manager or Admin seat.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}><ArrowLeft className="h-4 w-4 mr-1" /> Back to nursery</Button>
        </div>
      </div>
    );
  }

  const areaIds = new Set((areas ?? []).map((a) => a.id));
  const candidates = locations.filter((l) => !areaIds.has(l.id));
  const itemLabel = (s: NurseryStockItem) =>
    `${s.cultivar_trade_name || s.cultivar_name || 'Tree'}${s.rootstock_name ? ` / ${s.rootstock_name}` : ''} (${s.tree_type_name ?? s.tree_type_code ?? ''})`;

  return (
    <>
      <PageHeader
        title="Despatch areas"
        subtitle="Where pickers draw from, and how each is kept stocked from the growing farms."
        icon={<Warehouse className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" disabled={busy}
            onClick={() => run(async () => {
              const res = await nurseryService.checkRefills();
              if (!res.success) throw new Error(errorMessage(res, 'Could not check'));
              setNotice(res.data.raised.length ? `Raised ${res.data.raised.join(', ')}.` : 'Nothing needs refilling right now.');
            }, 'Could not check the areas')}>
            <RefreshCw className="h-4 w-4" /> Check now
          </Button>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}
        {notice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900">
            {notice} <Link to="/nursery/transfers" className="underline">Transfers</Link>
          </div>
        )}

        <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-0 sm:min-w-[16rem]">
            <label htmlFor="new-area" className="block text-sm font-medium text-neutral-700 mb-1">Make a location a despatch area</label>
            <select id="new-area" value={newArea} onChange={(e) => setNewArea(e.target.value ? Number(e.target.value) : '')} className={inputCls}>
              <option value="">Choose a location…</option>
              {candidates.map((l) => <option key={l.id} value={l.id}>{l.name}{l.farm_name ? ` — ${l.farm_name}` : ''}</option>)}
            </select>
          </div>
          <Button size="sm" disabled={busy || !newArea}
            onClick={async () => { if (await run(() => nurseryService.setDespatchArea(newArea as number, true), 'Could not add the area')) setNewArea(''); }}>
            <Plus className="h-4 w-4" /> Add area
          </Button>
        </div>

        {areas === null ? (
          !error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : areas.length === 0 ? (
          <p className="text-sm text-neutral-500 text-center py-6">No despatch areas yet.</p>
        ) : areas.map((area) => {
          const d = drafts[area.id] ?? EMPTY;
          const setD = (patch: Partial<RuleDraft>) => setDrafts((x) => ({ ...x, [area.id]: { ...d, ...patch } }));
          return (
            <section key={area.id} className="bg-white border border-neutral-200 rounded-xl" aria-label={area.name}>
              <header className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-neutral-100">
                <div className="min-w-0 flex-1">
                  <h2 className="font-semibold text-neutral-900 break-words">{area.name}</h2>
                  <p className="text-xs text-neutral-500 break-words">{[area.block_name, area.farm_name].filter(Boolean).join(' · ')}</p>
                </div>
                <Button variant="outline" size="sm" disabled={busy}
                  onClick={() => { if (window.confirm(`Stop treating ${area.name} as a despatch area? Its refill rules stay but stop running.`)) run(() => nurseryService.setDespatchArea(area.id, false), 'Could not remove the area'); }}>
                  Remove area
                </Button>
              </header>
              {area.rules.length > 0 && (
                <ul className="divide-y divide-neutral-100">
                  {area.rules.map((r) => <RuleRow key={r.id} rule={r} busy={busy} onDelete={() => run(() => nurseryService.deleteRefillRule(r.id), 'Could not remove the rule')} />)}
                </ul>
              )}
              <div className="px-4 py-3 border-t border-neutral-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 items-end">
                <div className="sm:col-span-2">
                  <label htmlFor={`r-item-${area.id}`} className="block text-xs text-neutral-500 mb-1">Tree</label>
                  <select id={`r-item-${area.id}`} value={d.stock_item_id} onChange={(e) => setD({ stock_item_id: e.target.value ? Number(e.target.value) : '' })} className={inputCls}>
                    <option value="">Choose…</option>
                    {stockItems.map((s) => <option key={s.id} value={s.id}>{itemLabel(s)}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor={`r-level-${area.id}`} className="block text-xs text-neutral-500 mb-1">Keep at</label>
                  <input id={`r-level-${area.id}`} type="number" min={1} value={d.target_quantity} placeholder="e.g. 100"
                    onChange={(e) => setD({ target_quantity: e.target.value ? Number(e.target.value) : '' })} className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`r-refill-${area.id}`} className="block text-xs text-neutral-500 mb-1">Bring at a time</label>
                  <input id={`r-refill-${area.id}`} type="number" min={1} value={d.refill_quantity} placeholder="e.g. 50"
                    onChange={(e) => setD({ refill_quantity: e.target.value ? Number(e.target.value) : '' })} className={inputCls} />
                </div>
                <div>
                  <label htmlFor={`r-src-${area.id}`} className="block text-xs text-neutral-500 mb-1">From</label>
                  <select id={`r-src-${area.id}`} value={d.source_farm_id} onChange={(e) => setD({ source_farm_id: e.target.value ? Number(e.target.value) : '' })} className={inputCls}>
                    <option value="">Whichever farm has most</option>
                    {farms.filter((f) => f.id !== area.farm_id).map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-5 flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-neutral-500">
                    {d.target_quantity && d.refill_quantity
                      ? `When fewer than ${Number(d.target_quantity) - Number(d.refill_quantity) + 1} are here, ${d.refill_quantity} more are sent for.`
                      : 'A refill is raised once the area is a whole refill below the level it keeps.'}
                  </p>
                  <Button size="sm" disabled={busy} onClick={() => addRule(area)}><Plus className="h-4 w-4" /> Add rule</Button>
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </>
  );
}

function RuleRow({ rule: r, busy, onDelete }: { rule: RefillRule; busy: boolean; onDelete: () => void }) {
  const pct = Math.min(100, Math.round((r.on_hand / r.target_quantity) * 100));
  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-neutral-900 break-words">{r.cultivar_name}{r.rootstock_name ? ` / ${r.rootstock_name}` : ''}</p>
          <p className="text-xs text-neutral-500 break-words">
            {r.tree_type_name} · keep {r.target_quantity}, bring {r.refill_quantity} at a time{r.source_farm_name ? ` from ${r.source_farm_name}` : ''}
          </p>
          <div className="mt-2 h-2 rounded-full bg-neutral-100 overflow-hidden" aria-label={`${r.on_hand} of ${r.target_quantity} here`}>
            <div className={cn('h-full', r.refill_due ? 'bg-amber-500' : 'bg-green-500')} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs mt-1 text-neutral-600">
            {r.on_hand} here
            {r.open_transfer ? (
              <> · refill <Link to={`/nursery/transfers/${r.open_transfer.id}`} className="underline text-primary-700">{r.open_transfer.transfer_number}</Link> on its way</>
            ) : r.refill_due ? ' · due for a refill' : ''}
          </p>
          {r.last_problem && (
            <p className="mt-1 flex items-start gap-1 text-xs text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> <span className="break-words">{r.last_problem}</span>
            </p>
          )}
        </div>
        <button type="button" aria-label={`Remove the rule for ${r.cultivar_name}`} disabled={busy} onClick={onDelete}
          className="p-1.5 rounded border border-neutral-200 text-neutral-500 hover:bg-neutral-50 disabled:opacity-40 flex-shrink-0">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  );
}

export default DespatchAreas;
